import { forwardRef, useState } from 'react'
import type { InputHTMLAttributes } from 'react'
import { Eye, EyeOff, LockKeyhole } from 'lucide-react'
import { Field } from '@/components/ui'

/**
 * Page-local password field with a visibility toggle.
 *
 * KIT GAP: the shared `Input` has no trailing-adornment slot, so this
 * composes `Field` (label/hint/error aria wiring) with the same `.it-input`
 * CSS hooks the kit uses, adding an Eye/EyeOff toggle button. If the kit
 * grows a `trailing` slot this file should be deleted.
 */

export interface PasswordInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'> {
  label?: string
  hint?: string
  error?: string
}

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  function PasswordInput({ label = 'Password', hint, error, disabled, ...rest }, ref) {
    const [visible, setVisible] = useState(false)

    return (
      <Field label={label} hint={hint} error={error}>
        {(control) => (
          <div
            className="it-input"
            data-invalid={error ? '' : undefined}
            data-disabled={disabled ? '' : undefined}
          >
            <span className="it-input__icon" aria-hidden="true">
              <LockKeyhole size={15} strokeWidth={1.75} />
            </span>
            <input
              ref={ref}
              type={visible ? 'text' : 'password'}
              disabled={disabled}
              {...control}
              {...rest}
            />
            <RevealToggle
              visible={visible}
              disabled={disabled}
              onToggle={() => setVisible((v) => !v)}
            />
          </div>
        )}
      </Field>
    )
  },
)

function RevealToggle({
  visible,
  disabled,
  onToggle,
}: {
  visible: boolean
  disabled?: boolean
  onToggle: () => void
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      aria-label={visible ? 'Hide password' : 'Show password'}
      aria-pressed={visible}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 28,
        height: 28,
        margin: 0,
        marginRight: -6,
        padding: 0,
        flex: 'none',
        border: 0,
        borderRadius: 'var(--r-xs)',
        background: 'transparent',
        color: 'var(--text-faint)',
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      {visible ? (
        <EyeOff size={15} strokeWidth={1.75} aria-hidden="true" />
      ) : (
        <Eye size={15} strokeWidth={1.75} aria-hidden="true" />
      )}
    </button>
  )
}
